import { ReactElement, useMemo } from 'react';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { ProjectRoleNEW, ProjectWithRole } from ':cloud/types/service_proto';

type ProjectPermissionRequirement = ProjectRoleNEW | ProjectRoleNEW[];

interface PermissionProjectProps {
  permission: ProjectPermissionRequirement;
  fallback?: ReactElement | ReactElement[] | null;
  matchAllPermissions?: boolean;
  children: ReactElement | ReactElement[];
}

function PermissionProject({
  permission,
  fallback = null,
  matchAllPermissions = false,
  children,
}: PermissionProjectProps): ReactElement | null {
  const { selectedProject: projectWithRole } = useAppState() as {
    selectedProject: ProjectWithRole | null;
  };

  const hasPermission = useMemo(() => {
    return (requiredPermission: ProjectPermissionRequirement): boolean => {
      const projectRole = projectWithRole?.userRole;

      if (!projectRole) return false;

      if (Array.isArray(requiredPermission)) {
        return matchAllPermissions
          ? requiredPermission.every((perm) => perm === projectRole)
          : requiredPermission.some((perm) => perm === projectRole);
      }
      return requiredPermission === projectRole;
    };
  }, [projectWithRole?.userRole, matchAllPermissions]);

  if (hasPermission(permission)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return Array.isArray(children) ? <>{children}</> : children;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return Array.isArray(fallback) ? <>{fallback}</> : fallback;
}

export default PermissionProject;
